/**
 * Pure copy composition for the live surfaces (design spec §4 copy table).
 *
 * Shared by the Home rail (pill + recap) AND the space-home slot (`LiveSpaceSlot`
 * reuses `composePillSegments`/`composeRecapMeta`/`composePillGoers` for its folded
 * pill + recap, and `composeSlotHelpableSignal`/`composeSlotGoingSignal` for its
 * rotating content row) — one module keeps every live string byte-identical across
 * surfaces (no duplication drift).
 *
 * Kept separate from the components so the byte-exact strings are unit-tested
 * without rendering. Tone-of-voice: names before counts, no urgency words, no
 * exclamation marks. The `soon` countdown reuses `formatStartsIn` (the single
 * source of truth for the locked "Starts in 2h" strings) and lowercases the
 * lead so the pill reads "{Event} starts in {x}".
 */
import { STARTING_SOON_LABEL } from '@components/live/EventPhaseBadge';
import {
  joinPillSegments,
  type LivePillGoers,
  type LivePillSegments,
} from '@components/live/livePillSegments';
import type { EventRead, UserPublicRead } from '@models/api';
import { formatEventWhen } from '@utils/datetime';
import { getEventGoingCount } from '@utils/eventGoingCount';
import {
  formatStartsIn,
  isWithinStartingSoonBand,
  type LiveEventPhase,
} from '@utils/eventLiveness';
import { firstName } from '@utils/string.utils';

/** First cached attendee name, if the roster is already in the payload (detail-only, §7). */
const firstGoerName = (event: EventRead): string | undefined =>
  event.preview_users?.[0]?.full_name?.trim() || undefined;

/** "starts in 2h" — lowercased lead of the locked `formatStartsIn` string (§4). */
const lowerLead = (phrase: string): string =>
  phrase.length === 0 ? phrase : phrase.charAt(0).toLowerCase() + phrase.slice(1);

const pluralOthers = (n: number): string => `${n} ${n === 1 ? 'other' : 'others'}`;

const pluralPosts = (n: number): string => `${n} ${n === 1 ? 'post' : 'posts'}`;

/**
 * The `upcoming` pill's phase status (Live Layer v2 §5), sourced from the shared
 * badge ladder so the pill and `EventPhaseBadge` never drift on BOTH the label
 * AND the boundary: it reads the SAME `isWithinStartingSoonBand` predicate the
 * badge reads (not a byte-copy of the `startingSoonLeadMs` math), and within the
 * band shows `EventPhaseBadge`'s own `STARTING_SOON_LABEL` constant (imported,
 * not retyped) — so a `<`-vs-`<=` tweak or a label edit in one place can never
 * desync the corner badge and the pill. Beyond the band it shows the event's own
 * date via the card family's `formatEventWhen` (Today / Tomorrow / "Tue 16 Jun",
 * in the VIEWER's zone — the pill is a list surface, design.md "Event lists use
 * the viewer's timezone"), guarded: that parser (Temporal) is stricter than
 * the phase parser (`new Date()`), so a `start_date` that classifies `upcoming`
 * but is Invalid under Temporal degrades to no cue (formatEventWhen returns '')
 * instead of throwing and blanking the pill. Carries the ` · ` separator so it reads as a
 * labelled cue after the name, distinct from soon's inline "starts in {x}" verb
 * phrase (whose running countdown would read "starts in 340h" for a far-future
 * event — the bug this branch exists to avoid).
 */
const upcomingStatus = (event: EventRead, now: Date): string => {
  if (isWithinStartingSoonBand(event, now)) {
    return ` · ${STARTING_SOON_LABEL}`;
  }
  // Viewer frame: the pill is a list surface (design.md, "Event lists use the
  // viewer's timezone").
  const { date } = formatEventWhen(event, now, 'viewer');
  return date ? ` · ${date}` : '';
};

/**
 * The one-line pill copy per the §4 table, split at its truncation joints
 * (`LivePillSegments` — name / status / going / posts) so `LivePill` can
 * ellipsize the name while the counts stay visible (T-5).
 *
 * - soon + goer:            `{Event} starts in {x}`
 * - soon + member (!going): `{Event} starts in {x} · {Name} +{n} going` (names when cached,
 *                           else `· {going_count} going`; a name with nobody left to count
 *                           collapses to `· {Name} is going` — never `+0`)
 * - upcoming (v2 §5):       same shape as soon, but the status is the badge-ladder cue instead
 *                           of the countdown — `{Event} · Starting soon` (within the
 *                           `startingSoonLeadMs` band) or `{Event} · {date}` (beyond it), and a
 *                           non-goer keeps the `· {Name} +{n} going` tail (see `upcomingStatus`)
 * - live + goer:            `{Event} is live · {going_count} going · {m} posts`
 *                           (posts gated to counts ≥ 1 and pluralized `1 post` / `{m} posts`)
 *
 * `postsCount` is the PER-VIEWER feed count the host fetched via
 * `useEventFeedCounts` (T-8: the pill must show the same number the event
 * feed shows; `null` = nothing to show — guest, omitted, resolved 0, or still
 * loading). While it is still loading (`postsPending`, authed first fetch
 * only) the live pill renders a skeleton in the posts slot; segments encode
 * that as `postsPending: true` with `posts: null`, so the a11y line and the
 * tight-row going hide (both keyed on a RESOLVED `posts`) stay untouched.
 * Posts stay live-only: soon variants ignore both inputs.
 *
 * The `live` line reads the same for every viewer, everywhere it renders. `useLiveEventsRail`'s
 * selection retired the v1 goers-only-live rule — a live entry is a pill for every member, not
 * only goers (the carousel ring is an additional cue now, not the sole one) — matching the
 * public meetverse row (recap/pill redesign §1), which already rendered it for guests and
 * non-goers too; nothing in the copy presumes attendance.
 */
export function composePillSegments(
  event: EventRead,
  phase: LiveEventPhase,
  now: Date,
  postsCount: number | null,
  postsPending = false,
): LivePillSegments {
  const goingCount = getEventGoingCount(event);

  if (phase === 'live') {
    const posts = postsCount !== null && postsCount >= 1 ? ` · ${pluralPosts(postsCount)}` : null;
    return {
      name: event.name,
      status: ' is live',
      going: ` · ${goingCount} going`,
      posts,
      // A resolved segment always wins over a stale pending flag, so the
      // "never true alongside a non-null posts" invariant holds by shape.
      postsPending: posts === null && postsPending,
    };
  }

  // Non-live phases (soon | upcoming) share ONE shape; only the status cue
  // differs — soon runs its live countdown, upcoming reads the badge-ladder
  // cue (Starting soon / date) so a far-future event never shows "starts in 340h".
  const status =
    phase === 'upcoming' ? upcomingStatus(event, now) : ` ${lowerLead(formatStartsIn(event, now))}`;
  if (event.is_going === true) {
    return { name: event.name, status, going: null, posts: null, postsPending: false };
  }

  // The named goer spends one count; clamp like every sibling composer so a cached
  // name with goingCount ≤ 1 (probe-sourced payloads can carry previews) reads
  // `{Name} is going`, never `+0 going` / `+-1 going`.
  const name = firstGoerName(event);
  const others = Math.max(goingCount - 1, 0);
  const goingSuffix = name
    ? others === 0
      ? `${name} is going`
      : `${name} +${others} going`
    : `${goingCount} going`;
  return {
    name: event.name,
    status,
    going: ` · ${goingSuffix}`,
    posts: null,
    postsPending: false,
  };
}

/**
 * The pill v2 goer stack's data (recap/pill redesign §1), phase-gated in ONE
 * place the way the hosts' live-only feed-counts batching owns the posts
 * rule: faces are LIVE-only (the red dot + faces shape is the live pill; a
 * soon pill's countdown text stays). Returns `preview_users` straight through — absent on
 * probe-sourced payloads, where `LivePill` falls back to the text form — plus
 * the full going count for the +N chip. Shared by the Home rail, the space
 * slot, and the meetverse row so the gate can't drift between them.
 */
export function composePillGoers(event: EventRead, phase: LiveEventPhase): LivePillGoers | null {
  if (phase !== 'live') {
    return null;
  }
  return { users: event.preview_users, totalCount: getEventGoingCount(event) };
}

/** The pill line as one string — `composePillSegments` joined (a11y + copy tests). */
export function composePillText(
  event: EventRead,
  phase: LiveEventPhase,
  now: Date,
  postsCount: number | null,
): string {
  return joinPillSegments(composePillSegments(event, phase, now, postsCount));
}

/**
 * The Home recap RAIL's section label (recap-label redesign): ONE muted label
 * above the going-scoped Home recap rail. `useLiveRecaps` filters `is_going`, so
 * every card beneath it is an event the viewer attended — the attendance claim
 * is truthful for the whole section. Replaces the former per-card eyebrow (the
 * owner's ruling: one quiet section title, not a label that grows every card).
 *
 * Home-rail ONLY. The Events-tab strip is view-scoped (it can hold ended events
 * the viewer did NOT attend), so it uses the neutral `RECAP_JUST_ENDED_LABEL`
 * instead — a blanket "You were there" there would be false. The space-home
 * recap omits a label entirely: its `composeRecapMeta` line already opens with
 * "You were there with {n} others", so a second one would duplicate (see
 * LiveCompactCard).
 */
export const RECAP_ATTENDED_LABEL = 'You were there';

/**
 * The Events-tab just-ended strip's section label (recap-label redesign): a
 * neutral, factual title above the strip's swipe row. The strip is VIEW-scoped
 * (`useEventsTabJustEnded` respects the tab's All/Saved/Going filter), so it can
 * contain just-ended events the viewer did NOT attend — an attendance claim
 * would be false. This label states only the recency fact the whole row shares.
 */
export const RECAP_JUST_ENDED_LABEL = 'Just ended';

/**
 * The recap card meta line (§4): `You were there with {Name} and {n} others`.
 *
 * `others` counts everyone besides you (you are the goer). With a cached name we spend one on
 * the name and count the rest; without one we fall back to the count-only form (§7 rule). Home's
 * list source has no `preview_users`, so the count-only branch is what renders there.
 */
export function composeRecapMeta(event: EventRead): string {
  const others = Math.max(getEventGoingCount(event) - 1, 0);
  const name = firstGoerName(event);

  if (name) {
    const remaining = Math.max(others - 1, 0);
    return remaining === 0
      ? `You were there with ${name}`
      : `You were there with ${name} and ${pluralOthers(remaining)}`;
  }

  return others === 0 ? 'You were there' : `You were there with ${pluralOthers(others)}`;
}

/**
 * The recap card v2 people segment (recap/pill redesign §2): `{n} people`,
 * pluralized (`1 person` — you), gated ≥1 by returning null (§0: counts never
 * render as 0; a recap you attended always has you, so null is defensive).
 * The facepile beside it carries the faces; this line carries the number for
 * everyone, screen readers included.
 */
export function composeRecapPeople(event: EventRead): string | null {
  const going = getEventGoingCount(event);
  if (going < 1) {
    return null;
  }
  return `${going} ${going === 1 ? 'person' : 'people'}`;
}

/**
 * The card posts segment (recap card v2 §2; space compact card §1.5): `{m}
 * posts` from the viewer's feed count (`useEventFeedCounts` — T-8 semantics:
 * `null` = guest, omitted, resolved 0, or still loading). Gated ≥1 by
 * returning null, pluralized `1 post`. One composer so every card surface
 * reads the same.
 */
export function composeRecapPosts(postsCount: number | null): string | null {
  if (postsCount === null || postsCount < 1) {
    return null;
  }
  return pluralPosts(postsCount);
}

/**
 * The space-slot rotating row's MATCHES signal (W2 §rotating-row, signal 1):
 * `You have {N} matches`, singular `1 match` (Dexter 2026-07-23 "matches" reframe
 * — was "You can help {N} people"; same gating, same teal styling, same rotation
 * slot, second person, no urgency, tone-of-voice unchanged). The `at this event`
 * suffix stays CUT on this card's compact row — it truncated, and the event
 * context is already the card. Returns null below 1 so the signal is simply
 * absent (the row then shows who's going, or nothing) — counts never render as 0.
 *
 * Space-card row ONLY. Other surfaces (the For-you section, etc.) keep their own,
 * longer helpable phrasing — this composer is not shared with them.
 */
export function composeSlotHelpableSignal(count: number): string | null {
  if (count < 1) {
    return null;
  }
  return count === 1 ? 'You have 1 match' : `You have ${count} matches`;
}

/**
 * The space-slot rotating row's WHO'S-GOING signal (W2 §rotating-row, signal 2),
 * count-dependent forms (Dexter 2026-07-23; overflow copy corrected 2026-07-24).
 *
 * TRUTHFULNESS INVARIANT: `preview_users` is the event's general going-set face
 * pile (creator-first, recent RSVPs — see the backend's `get_events_preview_users`),
 * NOT the viewer's connections. So the copy NEVER claims a social relationship it
 * has no data for — a guest has zero connections, yet the same set renders for
 * guests. It states only who is going and how many, never "connections".
 *
 * `goingCount` is the already-derived `getEventGoingCount(event)` (computed once
 * per cell and passed in, so the count is derived in ONE place — not re-derived
 * here). `namedGoers` are the SAME clamped `selectGoingFaces('named-only')`
 * selection the facepile renders, so the copy and the avatars can NEVER disagree;
 * the count clamps everything (never name more people than are going). Four forms:
 *
 *   1. one named goer, no overflow   → `{Name} is going`               (singular verb)
 *   2. two named goers, no overflow  → `{Name1} and {Name2} are going`
 *   3. an overflow (count > shown)   → `{Name1}, {Name2} +{N} are going` (names the
 *      shown goers, `+N` = the rest; N = count − shown — truthful, no "connections")
 *   4. no named goers (probe path)   → `{N} going`                     (count-only)
 *
 * `text === srText` for every form — the visible line already carries the full
 * count (names + `+N`, or the bare count), so assistive tech hears the whole story
 * with no separate sr phrasing. Null when the count is missing/non-finite or below
 * 1 (defensive guard) — the signal is simply absent, never `0` or a broken string.
 */
export function composeSlotGoingSignal(
  goingCount: number,
  namedGoers: readonly Pick<UserPublicRead, 'full_name'>[],
): { text: string; srText: string } | null {
  // Defensive guard (restored): a missing / non-finite count, or a count below 1,
  // yields NO signal — never "{undefined} going" or "{Name} and undefined are
  // going". getEventGoingCount's participant_count fallback normally supplies a
  // real number; this covers the payload that carries neither.
  if (!Number.isFinite(goingCount) || goingCount < 1) {
    return null;
  }
  // Clamp the shown names to the count — never name more people than are going (a
  // probe payload can carry more previews than the count).
  const shown = Math.min(namedGoers.length, goingCount);
  // Form 4 — no names to show (probe path / picture-less previews): count-only.
  if (shown === 0) {
    const text = `${goingCount} going`;
    return { text, srText: text };
  }
  // The first name of each shown goer, via the ONE shared `firstName` parser (so a
  // future multi-word / unicode fix lands everywhere at once), with the raw trimmed
  // name as the fallback the parser's contract prescribes.
  const names = namedGoers
    .slice(0, shown)
    .map(goer => firstName(goer.full_name) ?? goer.full_name.trim());
  // Form 3 — more goers than we can name: name the shown ones and carry the rest as
  // `+N`. Plain goers, not the viewer's connections (see the truthfulness invariant).
  if (goingCount > shown) {
    const text = `${names.join(', ')} +${goingCount - shown} are going`;
    return { text, srText: text };
  }
  // Forms 1 & 2 — name everyone going (the count and the faces agree exactly).
  const text = shown === 1 ? `${names[0]} is going` : `${names[0]} and ${names[1]} are going`;
  return { text, srText: text };
}

/**
 * The space-slot goer's row-3 status line (W2.7): a quiet, muted confirmation that
 * replaces the actionable CTA once you're going (upcoming/soon/live), so the goer
 * card balances the actionable ones at equal height. Second person, present,
 * calm — no exclamation (tone-of-voice). Also the meaning the retired teal "Going"
 * chip used to carry, now owned here alone.
 */
export const SLOT_GOING_STATUS_LABEL = "You're going";
